import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IktatgatoService } from '../iktatgato.service';
import { DataService } from '../../data.service';

/**
 *  Iktatás: új főszám vagy alszám meglévő ügyirathoz.
 *
 *  Az adatkör a POSZEIDON iktatólapját követi. A felső jelölők (főszámos,
 *  kimenő/belső, digitális, BKSZ, elektronikusan kiadmányozandó) nem díszítés:
 *  ezek döntik el, hogyan kell az iratot kezelni és kiküldeni – a BKSZ-jelölésű
 *  irat kerül a kézbesítési sorba, az elektronikusan kiadmányozandó pedig
 *  aláírásra vár, mielőtt kimehetne.
 *
 *  A sorszámot a szerver osztja ki tranzakcióban; itt csak az adatokat vesszük fel.
 *  Az érkeztetésből érkezve az űrlap előre ki van töltve.
 */
@Component({
  selector: 'app-ikt-iktatas',
  templateUrl: './iktatas.component.html',
  styleUrls: ['../iktatgato-layout/iktatgato-layout.component.scss'],
})
export class IktIktatasComponent implements OnInit {
  konyvek: any[] = [];
  terv: any[] = [];
  ugyiratok: any[] = [];
  ugyintezok: any[] = [];

  /** Partnerkereső: a törzsből és a tanulói/gondviselői/céges nyilvántartásból javasol. */
  partnerKereses = '';
  partnerTalalatok: any[] = [];
  partnerKeresFut = false;

  mod: 'foszam' | 'alszam' = 'foszam';
  ful: 'iktatas' | 'kiegeszito' = 'iktatas';
  urlap: any = {
    konyv: '', ugyirat: '', targy: '', irany: 'be', partner_nev: '', partner_cim: '', partner_id: '',
    hivatkozasi_szam: '', kelte: '', iktatva: '', hatarido: '', ugyintezo: '',
    tetelszam: '', adathordozo: 'papir', melleklet_db: 0, erkeztetes: '', masolat_hiteles: false,
    // Az iktatólap jelölői és kiegészítő mezői
    bksz: false, elektronikusan_kiadmanyozando: false, digitalis: false,
    rendelkezesi_nyilvantartas: false,
    vonalkod: '', ugyintezes_tipus: '', megjegyzes: '', kezbesites_prioritas: '',
    irat_kategoria: '', biztonsagi_minosites: '', szerelendo_ugyirat: '', szoveg: '',
    kapcsolodo_irat: '',
  };
  eredmeny: any = null;
  folyamatban = false;

  readonly UGYINTEZES_TIPUSOK = ['érdemi ügyintézés', 'tájékoztatás', 'továbbítás',
    'irattárba helyezés', 'adatszolgáltatás'];
  readonly KATEGORIAK = ['általános', 'tanügyi', 'gazdasági', 'személyügyi', 'hatósági', 'panasz'];
  readonly MINOSITESEK = ['nem minősített', 'korlátozott terjesztésű', 'bizalmas'];
  readonly PRIORITASOK = ['normál', 'sürgős', 'azonnal'];

  constructor(private ikt: IktatgatoService, private data: DataService,
              private route: ActivatedRoute, private router: Router) {}

  async ngOnInit() {
    const ma = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    this.urlap.iktatva = `${ma.getFullYear()}-${p(ma.getMonth() + 1)}-${p(ma.getDate())}`;
    this.urlap.ugyintezo = await this.ikt.ki();

    const qp = this.route.snapshot.queryParamMap;
    if (qp.get('erkeztetes')) {
      this.urlap.erkeztetes = qp.get('erkeztetes');
      this.urlap.targy = qp.get('targy') || '';
      this.urlap.partner_nev = qp.get('partner') || '';
      this.urlap.partner_id = qp.get('partner_id') || '';
      this.urlap.adathordozo = qp.get('adathordozo') || 'papir';
      this.urlap.digitalis = !!qp.get('digitalis');
    }
    if (qp.get('ugyirat')) { this.mod = 'alszam'; this.urlap.ugyirat = qp.get('ugyirat'); }

    try {
      this.konyvek = (await this.ikt.konyvek()).filter((k: any) => k.allapot === 'nyitva');
      if (this.konyvek.length && !this.urlap.konyv) this.urlap.konyv = this.konyvek[0].id;
      this.terv = await this.ikt.terv();
      this.ugyintezok = await this.ikt.ugyintezok();
      /*  AZ ELŐZMÉNY NEM CSAK FOLYAMATBAN LÉVŐ LEHET.
       *
       *  A bejövő igazolás jellemzően olyan ügyhöz érkezik, amit már elintéztek vagy
       *  irattárba tettek – a „folyamatban" szűrő épp azokat hagyta ki, amelyekre a
       *  kolléga alszámot akart volna iktatni. Ezért állapot szerint nem szűrünk; a
       *  listában viszont KIÍRJUK az állapotot, hogy látszódjon, lezárt ügyhöz iktat. */
      const u = await this.ikt.ugyiratok({});
      this.ugyiratok = u.ugyiratok || [];
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Nem sikerült betölteni az alapadatokat.', 'error');
    }

    /*  Ügyirattal érkezve (a hivatali kapus igazolás ide hozza: az igazolás a KIKÜLDÖTT
     *  irathoz tartozik) az alszám-listát is betöltjük, és a konkrét iratot kijelöljük –
     *  különben a kolléga megkapja az ügyiratot, de az iratot újra ki kellene keresnie. */
    if (this.urlap.ugyirat) {
      await this.ugyiratValt();
      const kapcs = qp.get('kapcsolodo_irat');
      if (kapcs && this.kapcsolodoIratok.some((i: any) => String(i.id) === String(kapcs))) {
        this.urlap.kapcsolodo_irat = kapcs;
      }
    }
  }

  /** Kimenő iránynál a kézbesítés kérdése merül fel, ezért ott ajánljuk fel a BKSZ-t. */
  iranyValt() {
    if (this.urlap.irany !== 'ki') { this.urlap.bksz = false; this.urlap.elektronikusan_kiadmanyozando = false; }
  }

  /*  ELŐZMÉNY-KERESŐ.
   *
   *  Legördülő listából egy tanévnyi ügyirat nem választható ki. A keresés iktatószámra,
   *  tárgyra és az ügyben szereplő SZEMÉLY nevére is talál (a kiszolgáló az alszámok
   *  partnernevében is néz) – egy igazolásnál rendszerint a tanuló neve az, amire a
   *  kolléga emlékszik, nem az ügy tárgya. */
  elozmenyKereses = '';
  elozmenyFut = false;
  private elozmenyIdozito: any = null;

  elozmenyKeres() {
    clearTimeout(this.elozmenyIdozito);
    this.elozmenyIdozito = setTimeout(async () => {
      const sz = this.elozmenyKereses.trim();
      this.elozmenyFut = true;
      try {
        const u = await this.ikt.ugyiratok(sz.length >= 2 ? { kereses: sz } : {});
        this.ugyiratok = u.ugyiratok || [];
        //  Egyetlen találatnál nem kell még egyszer kattintani: az lesz a főszám.
        if (sz.length >= 2 && this.ugyiratok.length === 1 && !this.urlap.ugyirat) {
          this.urlap.ugyirat = this.ugyiratok[0].id;
          await this.ugyiratValt();
        }
      } catch (e) { /* marad az előző lista */ } finally { this.elozmenyFut = false; }
    }, 350);
  }

  /*  MELYIK IRATHOZ TARTOZIK A KÜLDEMÉNY?
   *
   *  Az alszámos iktatás az ÜGYIRATHOZ köt – de egy beérkező igazolás nem az ügyhöz
   *  általában tartozik, hanem egy KONKRÉT irathoz: arra a határozatra válasz, azt a
   *  hiánypótlást teljesíti. Ezt eddig csak utólag, az ügyirat képernyőjén lehetett
   *  megadni, és a gyakorlatban elmaradt. Itt egy lépésben megvan.
   *
   *  Elhagyható: ha nem választasz iratot, az irat ugyanúgy az ügyirat következő
   *  alszáma lesz, csak nem mutat rá egy konkrét előzményre. */
  kapcsolodoIratok: any[] = [];
  kapcsolodoTolt = false;

  async ugyiratValt() {
    this.urlap.kapcsolodo_irat = '';
    this.kapcsolodoIratok = [];
    if (!this.urlap.ugyirat) return;
    this.kapcsolodoTolt = true;
    try {
      const u = await this.ikt.ugyirat(this.urlap.ugyirat);
      this.kapcsolodoIratok = u?.iratok || [];
    } catch (e) { this.kapcsolodoIratok = []; } finally { this.kapcsolodoTolt = false; }
  }

  /** „3. alszám · T/12-3/2026 · kimenő · Határozat…" – irány nélkül nem lehet eldönteni, mire válasz. */
  iratCimke(i: any): string {
    const irany = String(i?.irany || '') === 'ki' ? 'kimenő' : 'bejövő';
    const targy = String(i?.targy || '').trim();
    return `${i?.alszam}. alszám · ${i?.iktatoszam} · ${irany}`
      + (targy ? ` · ${targy.length > 48 ? `${targy.slice(0, 47)}…` : targy}` : '');
  }

  /** „T/12/2026 – Tárgy · elintézve · 2026" – az állapot azért kell, hogy lezárt ügy is látsszon. */
  elozmenyCimke(u: any): string {
    const allapot = String(u?.allapot || '').replace('_', ' ');
    return `${u?.iktatoszam} – ${u?.targy || '(tárgy nélkül)'}`
      + `${allapot && allapot !== 'folyamatban' ? ` · ${allapot}` : ''}`;
  }

  /** Partnerkeresés gépelés közben. Kevesebb mint 2 karakternél nem terheljük a szervert. */
  async partnerKeres() {
    const sz = this.partnerKereses.trim();
    if (sz.length < 2) { this.partnerTalalatok = []; return; }
    this.partnerKeresFut = true;
    try {
      const r = await this.ikt.partnerek(sz);
      this.partnerTalalatok = [...(r.partnerek || []), ...(r.javaslatok || [])].slice(0, 12);
    } catch {
      this.partnerTalalatok = [];
    } finally {
      this.partnerKeresFut = false;
    }
  }

  /**
   *  Partner kiválasztása. A javaslatot (tanuló, gondviselő, cég) egyben át is emeljük
   *  a saját törzsbe, hogy legközelebb már egy kattintás legyen, és az iraton a
   *  személy azonosítható maradjon – ne csak egy legépelt név.
   */
  async partnerValaszt(p: any) {
    this.urlap.partner_nev = p.nev;
    this.urlap.partner_cim = [p.irsz, p.telepules, p.cim].filter(Boolean).join(' ') || this.urlap.partner_cim;
    this.partnerKereses = '';
    this.partnerTalalatok = [];
    if (p.id) { this.urlap.partner_id = p.id; return; }
    try {
      const r = await this.ikt.partnerFelvesz({
        nev: p.nev, tipus: p.tipus, azonosito: p.azonosito || null,
        irsz: p.irsz || null, telepules: p.telepules || null, cim: p.cim || null,
        email: p.email || null, telefon: p.telefon || null,
        forras: p.forras, forras_azonosito: p.forras_azonosito,
      });
      this.urlap.partner_id = r.id;
    } catch { /* a név enélkül is rákerül az iratra */ }
  }

  async iktat() {
    if (!this.urlap.targy?.trim()) { this.data.sendToast('Az irat tárgya kötelező.', 'warning'); return; }
    if (this.mod === 'foszam' && !this.urlap.konyv) { this.data.sendToast('Válassz iktatókönyvet.', 'warning'); return; }
    if (this.mod === 'alszam' && !this.urlap.ugyirat) { this.data.sendToast('Válassz ügyiratot.', 'warning'); return; }

    this.folyamatban = true;
    try {
      const adat = { ...this.urlap };
      if (this.mod === 'foszam') delete adat.ugyirat; else delete adat.konyv;
      this.eredmeny = await this.ikt.iktat(adat);
      this.data.sendToast(`Iktatva: ${this.eredmeny.iktatoszam}`, 'success');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Az iktatás nem sikerült.', 'error');
    } finally {
      this.folyamatban = false;
    }
  }

  ugyiratra() { if (this.eredmeny) this.router.navigateByUrl('/iktatgato/ugyirat/' + this.eredmeny.ugyirat_id); }
  iratra() { if (this.eredmeny) this.router.navigateByUrl('/iktatgato/irat/' + this.eredmeny.irat_id); }
  ujra() { this.eredmeny = null; this.urlap.targy = ''; this.urlap.erkeztetes = ''; }
}
